import {
  AudioOutlined,
  CheckOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Tooltip, message } from "antd";
import React, { Suspense, useEffect, useRef, useState } from "react";

import { type PromptInputFile } from "@fitness-app/web-ui/components/ai-elements/prompt-input";

import { getRecordTime } from "~/components/AudioRecorder/AudioRecorder";
import { AudioVisualizer } from "~/components/AudioRecorder/AudioVisualizer";
import useAudioRecorder from "~/components/AudioRecorder/useAudioRecorder";

const LiveAudioVisualizer = React.lazy(async () => {
  const { LiveAudioVisualizer } = await import("react-audio-visualize");
  return { default: LiveAudioVisualizer };
});

type SalesAgentVoiceRecorderProps = {
  onCancel: () => void;
  onSend: (file: PromptInputFile) => void;
  onNotAllowed: () => void;
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert blob to a data URL"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Voice-message flow for the sales agent, mirroring the chat one (ChatInput +
 * AudioRecorder): recording starts immediately, can be paused, and stopping it
 * shows a playback preview — nothing is sent until the send button is pressed.
 */
export const SalesAgentVoiceRecorder = ({
  onCancel,
  onSend,
  onNotAllowed,
}: SalesAgentVoiceRecorderProps): React.ReactElement => {
  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
    mediaRecorder,
    setRecordingBlob,
  } = useAudioRecorder({ noiseSuppression: true, echoCancellation: true }, onNotAllowed);

  const [savedDuration, setSavedDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentPlayedTime, setCurrentPlayedTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardedRef = useRef(false);
  const startedRef = useRef(false);
  // Render-time assignment so the unmount cleanup below sees the CURRENT recorder
  // (an effect dependency would never fire after unmount).
  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined);
  mediaRecorderRef.current = mediaRecorder;

  // Start recording as soon as the panel opens (same as the chat flow).
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
      // Toggling back to typing mid-recording unmounts this panel — release the
      // microphone instead of leaving the browser's recording indicator on.
      discardedRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      recorder?.stream.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // The hook delivers the blob asynchronously after stopRecording(); when the stop
  // came from the discard button, drop it instead of showing the preview.
  useEffect(() => {
    if (recordingBlob && discardedRef.current) {
      discardedRef.current = false;
      setRecordingBlob(undefined);
    }
  }, [recordingBlob, setRecordingBlob]);

  // Object URLs are side effects — create/revoke them in an effect, not in render.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!recordingBlob) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(recordingBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recordingBlob]);

  const handleStopForPreview = () => {
    setSavedDuration(recordingTime);
    stopRecording();
  };

  const handleDiscard = () => {
    if (isRecording) {
      discardedRef.current = true;
      stopRecording();
    }
    onCancel();
  };

  const handleSend = async () => {
    if (!recordingBlob) return;
    try {
      const mediaType = (recordingBlob.type || "audio/webm").split(";")[0] ?? "audio/webm";
      const url = await blobToDataUrl(recordingBlob);

      onSend({
        id: crypto.randomUUID(),
        type: "file",
        mediaType,
        filename: `voice-message.${mediaType.includes("mp4") ? "m4a" : "webm"}`,
        url,
      });
    } catch {
      void message.error("Nie udało się przygotować nagrania do wysyłki");
    }
  };

  const startPlaybackTicker = () => {
    if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
    playbackIntervalRef.current = setInterval(() => {
      if (audioRef.current?.ended) {
        if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
        setCurrentPlayedTime(0);
      } else {
        setCurrentPlayedTime(audioRef.current?.currentTime ?? 0);
      }
    }, 100);
  };

  const hasPreview = Boolean(recordingBlob && previewUrl);

  return (
    <div
      className="flex min-h-[48px] w-full items-center gap-x-2 rounded-lg border border-gray-200 px-2 py-1"
      data-testid="sales_agent_voice_recorder"
    >
      <span className="font-bold tabular-nums">{getRecordTime(hasPreview ? savedDuration : recordingTime)}</span>

      <span className="flex w-full flex-1 items-center">
        {hasPreview && recordingBlob ? (
          <Suspense fallback={<></>}>
            <audio
              src={previewUrl ?? undefined}
              ref={audioRef}
              controls={false}
              autoPlay={false}
              style={{ height: 0, width: 0 }}
              onPlay={() => {
                startPlaybackTicker();
                setPlaying(true);
              }}
              onPause={() => {
                if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
                setPlaying(false);
              }}
              onEnded={() => {
                if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
                setPlaying(false);
                setCurrentPlayedTime(0);
              }}
            >
              <track default kind="captions" srcLang="pl" />
            </audio>
            <AudioVisualizer
              blob={recordingBlob}
              barWidth={2}
              gap={1}
              width={160}
              height={30}
              currentTime={currentPlayedTime}
            />
          </Suspense>
        ) : mediaRecorder ? (
          <Suspense fallback={<></>}>
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorder}
              barWidth={2}
              gap={2}
              width={150}
              height={24}
              fftSize={512}
              maxDecibels={-10}
              minDecibels={-80}
              smoothingTimeConstant={0.4}
            />
          </Suspense>
        ) : (
          <span className="text-gray-500">Przygotowuję mikrofon...</span>
        )}
      </span>

      {hasPreview ? (
        <>
          <Tooltip title={playing ? "Pauza" : "Odsłuchaj"}>
            {playing ? (
              <PauseCircleOutlined className="text-lg" onClick={() => audioRef.current?.pause()} />
            ) : (
              <PlayCircleOutlined className="text-lg" onClick={() => void audioRef.current?.play()} />
            )}
          </Tooltip>
          <Tooltip title="Odrzuć nagranie">
            <DeleteOutlined className="text-lg text-red-500" onClick={handleDiscard} />
          </Tooltip>
          <Tooltip title="Wyślij nagranie">
            <Button
              size="small"
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              onClick={() => void handleSend()}
            />
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title={isPaused ? "Wznów nagrywanie" : "Wstrzymaj"}>
            {isPaused ? (
              <AudioOutlined className="text-lg" onClick={togglePauseResume} />
            ) : (
              <PauseCircleOutlined className="text-lg" onClick={togglePauseResume} />
            )}
          </Tooltip>
          <Tooltip title="Zakończ i odsłuchaj">
            <Button
              size="small"
              type="primary"
              shape="circle"
              icon={<CheckOutlined />}
              disabled={!isRecording || recordingTime === 0}
              onClick={handleStopForPreview}
            />
          </Tooltip>
          <Tooltip title="Odrzuć nagranie">
            <DeleteOutlined className="text-lg text-red-500" onClick={handleDiscard} />
          </Tooltip>
        </>
      )}
    </div>
  );
};
